import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, PlayCircle, BellRing } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading } from "@/components/admin/ui";

const inputCls = "w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-foreground focus:border-gold focus:outline-none";

export default function AdminAccessReminders() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  useEffect(() => { api.get("/admin/access-reminders/settings").then((r) => setS(r.data)).catch(() => setS(false)); }, []);
  if (s === null) return <Loading />;
  if (!s) return <p className="text-muted-foreground">Unable to load settings.</p>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const num = (k, v) => set(k, Math.max(0, parseInt(v || 0, 10)));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/access-reminders/settings", s); setS(data); toast.success("Reminder settings saved."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const run = async () => {
    setRunning(true);
    try { const { data } = await api.post("/admin/access-reminders/run"); toast.success(`Reminders processed — ${data.created} draft(s) created, ${data.skipped} skipped.`); }
    catch (e) { toast.error(apiErr(e)); } finally { setRunning(false); }
  };

  const Toggle = ({ k, label, desc }) => (
    <label className="flex items-start justify-between gap-4 py-3 border-b border-white/5 cursor-pointer">
      <span><span className="text-sm">{label}</span>{desc && <span className="block text-xs text-muted-foreground mt-0.5">{desc}</span>}</span>
      <input type="checkbox" checked={!!s[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`reminder-${k}`} className="mt-1 h-4 w-4 accent-[#D4AF37]" />
    </label>
  );

  return (
    <div className="max-w-2xl" data-testid="admin-access-reminders">
      <AdminHeading title="Pending Activation Reminders" subtitle="Automated, human-controlled reminders for applicants awaiting approval. Off by default." />
      <Panel>
        <Toggle k="enabled" label="Enable reminders" desc="When enabled, eligible pending applicants become reminder candidates." />
        <Toggle k="require_approval" label="Require human approval before sending" desc="Creates a Draft in Communications instead of sending." />
        <Toggle k="auto_queue" label="Auto-queue when approved automation is enabled" desc="Only used when human approval is off." />
        <Toggle k="stop_on_response" label="Stop after applicant responds or status changes" />
        <div className="grid grid-cols-3 gap-4 mt-5">
          <div><label className="text-[10px] tracking-label uppercase text-muted-foreground">Days after request</label><input type="number" className={inputCls} value={s.days_after} onChange={(e) => num("days_after", e.target.value)} data-testid="reminder-days" /></div>
          <div><label className="text-[10px] tracking-label uppercase text-muted-foreground">Frequency (days)</label><input type="number" className={inputCls} value={s.frequency_days} onChange={(e) => num("frequency_days", e.target.value)} /></div>
          <div><label className="text-[10px] tracking-label uppercase text-muted-foreground">Max reminders</label><input type="number" className={inputCls} value={s.max_count} onChange={(e) => num("max_count", e.target.value)} /></div>
        </div>
        <div className="text-xs text-muted-foreground mt-4">Template: <span className="text-gold">{s.template_key}</span></div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} disabled={saving} className="btn-gold px-5 py-2.5 text-sm inline-flex items-center gap-2" data-testid="reminder-save"><Save size={15} /> Save settings</button>
          <button onClick={run} disabled={running || !s.enabled} className="btn-ghost-gold px-5 py-2.5 text-sm inline-flex items-center gap-2" data-testid="reminder-run"><PlayCircle size={15} /> Run reminder cycle now</button>
        </div>
        <p className="text-[11px] text-muted-foreground/70 mt-3 flex items-center gap-1.5"><BellRing size={12} /> Reminders never send automatically unless approval is off and auto-queue is on. Drafts appear in Communications → Review &amp; Release.</p>
      </Panel>
    </div>
  );
}
